import { useCallback, useEffect, useRef } from 'react';
import type { RevealTarget } from './reveal-target';

const PHONE = '(max-width: 767px)';

/** Breathing room between the revealed control and the edge it is brought to. */
const GAP = 12;

/**
 * Scrolls a phone to whatever the customer has to answer next once they have answered something —
 * on a phone the city, the times and the button row are all below the fold of the choice before
 * them, and nothing on screen says there is more to do.
 *
 * The URL holds the wizard's answers, and the router applies a change to it in a transition, so the
 * control that change reveals (the city under a picked service) is not in the page when the handler
 * returns. The scroll therefore waits for the next `searchParams`. An answer that leaves the URL as
 * it was — tapping the card already chosen, ticking consent — has no render to wait for.
 */
export function useRevealOnPhone(searchParams: URLSearchParams) {
    const pending = useRef<RevealTarget | null>(null);

    useEffect(() => {
        const target = pending.current;

        if (target !== null) {
            pending.current = null;
            revealOnPhone(target);
        }
    }, [searchParams]);

    return useCallback(
        (target: RevealTarget, changes: Record<string, string | null> = {}) => {
            const changesUrl = Object.entries(changes).some(([key, value]) => searchParams.get(key) !== value);

            if (changesUrl) {
                pending.current = target;
            } else {
                requestAnimationFrame(() => revealOnPhone(target));
            }
        },
        [searchParams]
    );
}

/**
 * Scrolls only when the control is not already in full view, and only as far as it takes: down to
 * its bottom edge, or up to its top — never so far that its top slides under the sticky header,
 * which is what a control taller than the screen (a day's times) is aligned by instead.
 */
function revealOnPhone(target: RevealTarget): void {
    if (!window.matchMedia(PHONE).matches) {
        return;
    }

    const element = document.querySelector(`[data-reveal="${target}"]`);

    if (element === null) {
        return;
    }

    const header = document.querySelector('.book-page .hdr')?.getBoundingClientRect().bottom ?? 0;
    const top = Math.max(header, 0) + GAP;
    const bottom = (window.visualViewport?.height ?? window.innerHeight) - GAP;
    const rect = element.getBoundingClientRect();

    if (rect.top >= top && rect.bottom <= bottom) {
        return;
    }

    const distance = rect.top < top ? rect.top - top : Math.min(rect.bottom - bottom, rect.top - top);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollBy({ top: distance, behavior: reduceMotion ? 'auto' : 'smooth' });
}
